import { useEffect, useState } from 'react'
import { Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { requestPhoneChange, verifyPhoneChange } from '@/lib/members'
import { normalizeKenyaPhone } from '@riverpark/utils/member-import'
import { getApiErrorMessage } from '@/lib/api'

type SmsDelivery = 'sent' | 'failed' | 'skipped' | 'not_sent' | 'logged_only'
type Step = 'idle' | 'sent'

interface PhoneAcknowledge {
  smsDelivery?: SmsDelivery
  smsError?: string | null
  devOtp?: string | null
  atSandbox?: boolean
}

interface ProfilePhoneVerifyProps {
  phone: string
  savedPhone: string
  onVerifiedChange: (verified: boolean) => void
}

function maskPhone(phone: string): string {
  if (phone.length < 8) return phone
  return `${phone.slice(0, 4)}···${phone.slice(-3)}`
}

function SmsStatusCard({ phone, ack }: { phone: string; ack: PhoneAcknowledge }) {
  const { smsDelivery, smsError, devOtp, atSandbox } = ack
  const sandboxNote = atSandbox ? (
    <p className="mt-2 text-xs leading-relaxed opacity-90">
      Sandbox mode — SMS only reaches numbers under Africa&apos;s Talking → Sandbox.
    </p>
  ) : null

  if (smsDelivery === 'sent') {
    return (
      <div
        className="rounded border border-status-paid-bg bg-status-paid-bg/40 px-4 py-3 font-sans text-sm text-status-paid-text"
        role="status"
      >
        <p className="font-medium">{atSandbox ? 'Sandbox SMS sent' : 'SMS sent'}</p>
        <p className="mt-1 text-xs leading-relaxed">
          A 6-digit code was sent to <strong>{maskPhone(phone)}</strong>. It expires in 10 minutes.
        </p>
        {sandboxNote}
      </div>
    )
  }

  if (smsDelivery === 'failed') {
    return (
      <div className="space-y-3">
        <div
          className="rounded border border-destructive/30 bg-destructive/10 px-4 py-3 font-sans text-sm text-destructive"
          role="alert"
        >
          <p className="font-medium">SMS could not be delivered</p>
          {smsError && <p className="mt-1 text-xs leading-relaxed">{smsError}</p>}
          {sandboxNote}
        </div>
        {devOtp && (
          <p className="font-sans text-xs text-status-pending-text">
            Dev code: <strong className="text-sm">{devOtp}</strong>
          </p>
        )}
      </div>
    )
  }

  if (smsDelivery === 'skipped' || smsDelivery === 'logged_only') {
    return (
      <div className="space-y-3">
        <div
          className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-4 py-3 font-sans text-sm text-status-pending-text"
          role="status"
        >
          <p className="font-medium">
            {smsDelivery === 'skipped' ? 'SMS blocked (dev safety)' : 'SMS not configured'}
          </p>
          <p className="mt-1 text-xs leading-relaxed">No message was sent to {maskPhone(phone)}.</p>
        </div>
        {devOtp && (
          <p className="font-sans text-xs text-status-pending-text">
            Dev code: <strong className="text-sm">{devOtp}</strong>
          </p>
        )}
      </div>
    )
  }

  return null
}

export function ProfilePhoneVerify({
  phone,
  savedPhone,
  onVerifiedChange,
}: ProfilePhoneVerifyProps) {
  const normalizedPhone = normalizeKenyaPhone(phone.trim())
  const normalizedSaved = normalizeKenyaPhone(savedPhone.trim())
  const phoneChanged = normalizedPhone.length > 0 && normalizedPhone !== normalizedSaved

  const [step, setStep] = useState<Step>('idle')
  const [verifiedForPhone, setVerifiedForPhone] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [ack, setAck] = useState<PhoneAcknowledge | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [requestPending, setRequestPending] = useState(false)
  const [verifyPending, setVerifyPending] = useState(false)

  const isVerified = phoneChanged && verifiedForPhone === normalizedPhone

  useEffect(() => {
    onVerifiedChange(phoneChanged ? isVerified : true)
  }, [phoneChanged, isVerified, onVerifiedChange])

  useEffect(() => {
    if (!phoneChanged) {
      setStep('idle')
      setVerifiedForPhone(null)
      setCode('')
      setAck(null)
      setError(null)
    }
  }, [phoneChanged])

  if (!phoneChanged) return null

  const canRequest = /^\+254\d{9}$/.test(normalizedPhone)

  async function handleRequest() {
    setError(null)
    setRequestPending(true)
    setVerifiedForPhone(null)
    try {
      const result = await requestPhoneChange(phone.trim())
      setAck({
        smsDelivery: result.smsDelivery,
        smsError: result.smsError,
        devOtp: result.devOtp,
        atSandbox: result.atSandbox,
      })
      setStep('sent')
      setCode('')
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setRequestPending(false)
    }
  }

  async function handleVerify() {
    if (code.length !== 6) return
    setError(null)
    setVerifyPending(true)
    try {
      await verifyPhoneChange(phone.trim(), code.trim())
      setVerifiedForPhone(normalizedPhone)
      setStep('idle')
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setVerifyPending(false)
    }
  }

  if (isVerified) {
    return (
      <div
        className="rounded border border-status-paid-bg bg-status-paid-bg/40 px-4 py-3 font-sans text-sm text-status-paid-text"
        role="status"
      >
        <p className="font-medium">Phone number verified</p>
        <p className="mt-1 text-xs leading-relaxed">
          You can save your profile to update your phone to {maskPhone(normalizedPhone)}.
        </p>
      </div>
    )
  }

  return (
    <div
      className="space-y-4 rounded border-2 border-primary/25 bg-green-pale/50 px-5 py-5"
      role="region"
      aria-label="Phone verification required"
    >
      <div className="flex gap-3">
        <div
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
          aria-hidden
        >
          <Smartphone size={20} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-sans text-sm font-semibold text-ink">Verify new phone number</p>
          <p className="mt-1 font-sans text-sm leading-relaxed text-muted-foreground">
            Save is blocked until you confirm{' '}
            <strong className="font-medium text-ink">{maskPhone(normalizedPhone)}</strong>. We will
            text a 6-digit code to that number.
          </p>
        </div>
      </div>

      {step === 'idle' && (
        <Button
          type="button"
          className="w-full sm:w-auto"
          loading={requestPending}
          disabled={!canRequest}
          onClick={() => void handleRequest()}
        >
          Send verification code
        </Button>
      )}

      {!canRequest && step === 'idle' && (
        <p className="font-sans text-sm text-muted-foreground">
          Enter a valid number in <strong className="font-medium text-ink">+254XXXXXXXXX</strong>{' '}
          format to send a code.
        </p>
      )}

      {step === 'sent' && ack && <SmsStatusCard phone={normalizedPhone} ack={ack} />}

      {step === 'sent' && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[140px] flex-1">
            <Input
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleVerify()
                }
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
            />
          </div>
          <Button
            type="button"
            loading={verifyPending}
            disabled={code.length !== 6}
            className="min-w-[7rem]"
            onClick={() => void handleVerify()}
          >
            Verify code
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={requestPending}
            onClick={() => void handleRequest()}
          >
            Resend code
          </Button>
        </div>
      )}

      {error && (
        <p className="font-sans text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
